/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'primereact/tag';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useDebounce } from '../../hooks/useDebounce';
import { recipientListsService, type RecipientList } from '../../services/recipient-lists.service';
import { totalOf, NEWEST_FIRST, sortParams, type SortState } from '../../services/notification-engine.types';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { extractErrorMessage } from '../../utils/error-handler';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { ProjectCell } from '../../components/common/project-cell';
import { useAccessibleProjects } from '../../hooks/useProjectScope';
import ProjectFilter from '../../components/common/project-filter';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import Icon from '../../components/common/icon';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';

/** The two things a list is for — a plain audience, or a do-not-contact set. */
const USED_FOR_OPTIONS = [
  { label: 'Audience', value: false },
  { label: 'Do not contact', value: true },
];

const RecipientListList = ({ embedded = false }: { embedded?: boolean }) => {
  const { navigateToCreate, navigateToView, navigateToEdit } = useSecureNavigation({
    entity: 'recipient list', basePath: '/recipient-lists',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.RECIPIENT_LIST);
  const canUpdate = useCanUpdate(JOB_NAMES.RECIPIENT_LIST);
  const canDelete = useCanDelete(JOB_NAMES.RECIPIENT_LIST);

  // Which project each row belongs to. The COLUMN always shows — "this project"
  // versus the shared "Global" rows is worth seeing even to someone with one
  // project — while the filter beside it only appears when there is a choice.
  const { projects, canScopeByProject } = useAccessibleProjects();

  const [rows, setRows] = useState<RecipientList[]>([]);
  const [loading, setLoading] = useState(true);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  // Server-side sort: the table asks, the API orders. Sorting in the browser
  // would only order the rows already fetched, which is not what a sort control
  // appears to promise. Newest first until the viewer picks a column.
  const [sort, setSort] = useState<SortState>(NEWEST_FIRST);
  const [toDelete, setToDelete] = useState<RecipientList | null>(null);

  // Filters. Debounced search over code + description; the "used for" dropdown
  // separates plain audiences from do-not-contact lists. Both are server-side.
  const [search, setSearch] = useState('');
  const [usedFor, setUsedFor] = useState<boolean | null>(null);
  const [appId, setAppId] = useState<string | null>(null);
  const [showCore, setShowCore] = useState(false);
  const debouncedSearch = useDebounce(search, 400);

  const showError = useCallback((m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 8000 }), []);
  const showOk = useCallback((m: string) => toast.current?.show({ severity: 'success', summary: 'Success', detail: m, life: 3000 }), []);

  const fetchRows = useCallback(async (skip = 0, limit = pageSize, sorting: SortState = sort) => {
    try {
      setLoading(true);
      const resp = await recipientListsService.list({
        skip, limit, ...sortParams(sorting),
        ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        ...(usedFor !== null ? { isDoNotContact: usedFor } : {}),
        ...(appId ? { appId } : {}),
        include_global: showCore,
      });
      setRows(resp.data || []);
      setTotal(totalOf(resp));
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to load recipient lists'));
    } finally {
      setLoading(false);
    }
  }, [pageSize, sort, showError, debouncedSearch, usedFor, appId, showCore]);

  // First load, and again on any filter change — always from page one, so a
  // narrowed result cannot strand the viewer on a page that no longer exists.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFirst(0); fetchRows(0, pageSize); }, [debouncedSearch, usedFor, appId, showCore]);

  const handlePage = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(e.first, e.rows); };

  const handleSort = (e: any) => {
    const next: SortState = { field: e.sortField || null, order: e.sortOrder || null };
    setSort(next);
    setFirst(0);
    fetchRows(0, pageSize, next);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await recipientListsService.softDelete(toDelete._id);
      setToDelete(null);
      showOk('Recipient list deleted');
      fetchRows(first, pageSize);
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to delete recipient list'));
    }
  };

  const columns: SaasColumn<RecipientList>[] = useMemo(() => [
    { field: 'recipientListCode', header: 'Code', sortable: true, body: (r) => <span className="font-mono text-xs">{r.recipientListCode}</span> },
    {
      // Only an ACTIVE do-not-contact list suppresses anybody, so a
      // deactivated one tagged "DO NOT CONTACT" reads as protecting people it
      // is not protecting.
      field: 'isDoNotContact', header: 'Used for', sortable: true,
      body: (r) => {
        if (!r.isDoNotContact) {
          return <Tag value="Audience" severity="secondary" className="text-xs px-1.5 py-0.5" />;
        }
        return r.isActive !== false
          ? <Tag value="Do not contact" severity="danger" className="text-xs px-1.5 py-0.5" />
          : <span className="inline-flex flex-col">
              <Tag value="Do not contact" severity="secondary" className="text-xs px-1.5 py-0.5" />
              <span className="text-xs text-gray-400 mt-0.5">deactivated — suppresses nobody</span>
            </span>;
      },
    },
    { field: 'parties', header: 'Parties', body: (r) => <span className="text-xs">{(r.parties || []).length}</span> },
    { field: 'description', header: 'Description', sortable: true, body: (r) => <span className="text-sm">{r.description || '-'}</span> },
    {
      field: 'appId', header: 'Project', style: { minWidth: 150 },
      body: (r: RecipientList) => <ProjectCell appId={r.appId} projects={projects} />,
    },
    { field: 'createdAt', header: 'Created', sortable: true, body: (r) => <span className="text-xs text-gray-500 whitespace-nowrap">{r.createdAt ? formatTimestamp(r.createdAt) : '-'}</span> },
    {
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => (
        <div className="flex gap-1 justify-center">
          <button aria-label="View" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToView(r._id)} title="View">
            <Icon name="eye" size={17} className="text-green-600 dark:text-green-400" />
          </button>
          {canUpdate && <button aria-label="Edit" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToEdit(r._id)} title="Edit">
            <Icon name="pencil" size={17} className="text-blue-600 dark:text-blue-400" />
          </button>}
          {canDelete && <button aria-label="Delete" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setToDelete(r)} title="Delete">
            <Icon name="trash" size={17} className="text-red-600 dark:text-red-400" />
          </button>}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canUpdate, canDelete, canScopeByProject, projects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.RECIPIENT_LIST}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-RecipientList-List">
        {!embedded && (
          <PageHeader icon="pi pi-users" title="Recipient Lists" subtitle="Named, reusable groups of parties — and the do-not-contact lists that suppress them" testId="RecipientList.Header" />
        )}
        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="recipient-list" columns={columns} data={rows} loading={loading} dataKey="_id"
            emptyMessage="No recipient lists found" first={first} rows={pageSize} totalRecords={total}
            onPage={handlePage} rowsPerPageOptions={[5, 10, 25, 50]} sortField={sort.field || undefined} sortOrder={sort.order} onSort={handleSort} onFilter={() => {}} testId="RecipientListList"
            globalSearch={search} onGlobalSearchChange={setSearch} globalSearchPlaceholder="Search code or description..."
            toolbarStart={
              <div className="flex flex-wrap items-center gap-2">
                <ProjectFilter value={appId} onChange={setAppId} testId="RecipientListList.Filter.Project" />
                <Dropdown value={usedFor} options={USED_FOR_OPTIONS} onChange={(e: any) => setUsedFor(e.value ?? null)} placeholder="Used for" showClear style={{ minWidth: '160px' }} data-testid="RecipientListList.Filter.UsedFor" />
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="RecipientListList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={<>
              <span className="text-sm text-gray-400 whitespace-nowrap">{total} {total === 1 ? 'record' : 'records'}</span>
              <RefreshButton onClick={() => fetchRows(first, pageSize)} loading={loading} testId="RecipientListList.Refresh" />
              {canCreate && <Button label="Create List" icon="pi pi-plus" onClick={() => navigateToCreate()} size="small" />}
            </>}
          />
        </div>
      </div>
      <DeleteConfirmDialog visible={!!toDelete} onCancel={() => setToDelete(null)} onDelete={handleDelete}
        message={`Delete recipient list '${toDelete?.recipientListCode}'?`} testId="RecipientListList.Delete" />
    </PermissionGuard>
  );
};

export default RecipientListList;
